import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatMoney } from "@/lib/currency";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import Modal from "@/components/Modal";
import MonthDropdown from "@/components/MonthDropdown";
import { Field, Th, Td, SummaryStat } from "@/pages/Income";
import { Plus, Pencil, Trash2, CheckCircle2, Bell, BellOff, Send } from "lucide-react";
import MoneyBurst from "@/components/MoneyBurst";
import DateField from "@/components/DateField";

export default function Bills() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [burstBillId, setBurstBillId] = useState(null); // which bill row is currently bursting
  const [reminderBill, setReminderBill] = useState(null); // bill open in the reminder dialog
  const [form, setForm] = useState({ name:"", amount:"", due_date:new Date().toISOString().slice(0,10), category:"Utilities", is_paid:false, is_recurring:false, frequency:"monthly" });

  const load = async () => { const { data } = await api.get("/bills"); setItems(data); };
  useEffect(() => { load(); }, []);

  const startNew = () => { setEdit(null); setForm({ name:"", amount:"", due_date:new Date().toISOString().slice(0,10), category:"Utilities", is_paid:false, is_recurring:false, frequency:"monthly" }); setOpen(true); };
  const startEdit = (i) => { setEdit(i); setForm({ ...i }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    const payload = { ...form, amount: parseFloat(form.amount) };
    try {
      if (edit) await api.put(`/bills/${edit.id}`, payload); else await api.post("/bills", payload);
      toast.success(t("common.toast_saved")); setOpen(false); load();
    } catch { toast.error(t("common.toast_failed")); }
  };
  const markPaid = async (b) => {
    await api.put(`/bills/${b.id}`, { ...b, is_paid: true });
    toast.success(t("toasts.bill_marked_paid"));
    setBurstBillId(b.id);                              // fire the row's burst
    setTimeout(() => { setBurstBillId(null); load(); }, 500);
  };
  const remove = async (id) => { if (!confirm("Delete bill?")) return; await api.delete(`/bills/${id}`); toast.success(t("common.toast_deleted")); load(); };

  const cur = user?.currency || "USD";
  const today = new Date().toISOString().slice(0, 10);
  const currentYm = today.slice(0, 7);
  const [selectedMonth, setSelectedMonth] = useState(currentYm);

  // Filter bills to the picked month by due_date; keep the natural
  // "unpaid first, paid after" order within the month.
  const rowsInMonth = useMemo(() => {
    const inMonth = items.filter(i => (i.due_date || "").startsWith(selectedMonth));
    const upcomingM = inMonth.filter(i => !i.is_paid).sort((a, b) => a.due_date.localeCompare(b.due_date));
    const paidM = inMonth.filter(i => i.is_paid);
    return upcomingM.concat(paidM);
  }, [items, selectedMonth]);

  // Summary cards keep their old (all-time / this cycle) meaning so we don't
  // regress the dashboard-style stats users are already used to.
  const upcoming = items.filter(i => !i.is_paid);
  const paid = items.filter(i => i.is_paid);
  const totalDue = upcoming.reduce((a, b) => a + Number(b.amount || 0), 0);
  const monthTotal = rowsInMonth.reduce((a, b) => a + Number(b.amount || 0), 0);

  return (
    <div>
      <PageHeader
        subtitle={t("pages.bills.subtitle")}
        title={t("pages.bills.title")}
        action={<button className="btn-primary" onClick={startNew} data-testid="add-bill-btn"><Plus size={16}/> {t("pages.bills.add")}</button>}
      />

      <div className="grid md:grid-cols-3 gap-5 mb-6 stagger">
        <SummaryStat label={t("pages.bills_body.upcoming_bills")} value={upcoming.length}/>
        <SummaryStat label={t("pages.bills_body.total_due")} value={formatMoney(totalDue, cur)}/>
        <SummaryStat label={t("pages.bills_body.paid_this_cycle")} value={paid.length}/>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <label className="text-sm text-[var(--text-secondary)]">Month</label>
        <MonthDropdown
          items={items}
          dateKey="due_date"
          value={selectedMonth}
          onChange={setSelectedMonth}
          testid="bill-month-picker"
        />
      </div>

      {items.length === 0 ? (
        <EmptyState title={t("pages.bills.empty_title")} body={t("pages.bills_body.empty_body")}
          action={<button className="btn-primary" onClick={startNew}><Plus size={16}/> {t("pages.bills_body.add_bill")}</button>}/>
      ) : rowsInMonth.length === 0 ? (
        <EmptyState
          title="No bills this month"
          body="Pick a different month from the dropdown, or add a new bill."
          action={<button className="btn-primary" onClick={startNew}><Plus size={16}/> {t("pages.bills_body.add_bill")}</button>}
        />
      ) : (
        <div className="card-flat overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[var(--bg-secondary)] text-[var(--text-secondary)]">
              <tr><Th>{t("pages.bills_body.bill_h")}</Th><Th>{t("pages.bills_body.category_h")}</Th><Th>{t("pages.bills_body.due_date_h")}</Th><Th>{t("pages.bills_body.status_h")}</Th><Th className="text-right">{t("pages.bills_body.amount_h")}</Th><Th></Th></tr>
            </thead>
            <tbody>
              {rowsInMonth.map(b => {
                const overdue = !b.is_paid && b.due_date < today;
                return (
                  <tr key={b.id} className="border-t border-[var(--border-default)] hover:bg-[var(--bg-secondary)]/60">
                    <Td>
                      <div className="font-medium">{b.name}</div>
                      {b.is_recurring && <span className="badge badge-neutral">{t("pages.bills_body.recurring_freq", { cycle: b.frequency })}</span>}
                    </Td>
                    <Td>{b.category}</Td>
                    <Td className={overdue ? "text-[var(--accent-danger)] font-medium" : "text-[var(--text-secondary)]"}>{b.due_date}{overdue && t("pages.bills_body.overdue_suffix")}</Td>
                    <Td>{b.is_paid ? <span className="badge badge-success">{t("pages.bills_body.paid_badge")}</span> : overdue ? <span className="badge badge-danger">{t("pages.bills_body.overdue_badge")}</span> : <span className="badge badge-warn">{t("pages.bills_body.pending_badge")}</span>}</Td>
                    <Td className="text-right num-md">{formatMoney(b.amount, cur)}</Td>
                    <Td className="text-right relative">
                      <MoneyBurst trigger={burstBillId === b.id ? burstBillId : 0} tone="neg" />
                      {!b.is_paid && <button className="btn-danger-ghost text-[var(--accent-positive)]" onClick={() => markPaid(b)} title={t("pages.bills_body.mark_paid")} data-testid={`pay-bill-${b.id}`}><CheckCircle2 size={16}/></button>}
                      <button
                        className="btn-danger-ghost"
                        onClick={() => setReminderBill(b)}
                        title={b.reminder_enabled ? t("pages.bills_body.reminders_on", { days: b.reminder_days_ahead || 3 }) : t("pages.bills_body.set_reminder")}
                        data-testid={`reminder-bill-${b.id}`}
                      >
                        {b.reminder_enabled
                          ? <Bell size={15} color="var(--accent-warning)" fill="var(--accent-warning)" fillOpacity="0.25" />
                          : <BellOff size={15} />}
                      </button>
                      <button className="btn-danger-ghost" onClick={() => startEdit(b)}><Pencil size={15}/></button>
                      <button className="btn-danger-ghost" onClick={() => remove(b.id)}><Trash2 size={15}/></button>
                    </Td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-[var(--bg-secondary)] border-t-2 border-[var(--border-default)]">
              <tr>
                <td colSpan={4} className="px-4 py-3 text-sm font-semibold text-[var(--text-primary)]">
                  Total for {new Date(`${selectedMonth}-01T00:00:00`).toLocaleString(undefined, { month: "long", year: "numeric" })}
                </td>
                <td className="px-4 py-3 text-right num-md text-sm font-semibold text-[var(--text-primary)]" data-testid="bill-month-total">
                  {formatMoney(monthTotal, cur)}
                </td>
                <td />
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title={edit ? t("pages.bills_body.edit_title") : t("pages.bills_body.add_title")}>
        <form onSubmit={save} className="grid gap-4">
          <Field label={t("pages.bills_body.bill_name")}><input required className="field-input" value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="input-bill-name"/></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label={t("pages.bills_body.amount_l", { cur })}><input type="number" step="0.01" required className="field-input" value={form.amount} onChange={e => setForm({...form, amount: e.target.value})} data-testid="input-bill-amount"/></Field>
            <Field label={t("pages.bills_body.due_date_l")}><DateField value={form.due_date} onChange={v => setForm({...form, due_date: v})} testid="input-bill-date"/></Field>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Field label={t("common.category")}>
              <select className="field-input" value={form.category} onChange={e => setForm({...form, category: e.target.value})}>
                {["Utilities","Insurance","Rent","Tax","Membership","Loan","Other"].map(c => <option key={c}>{c}</option>)}
              </select>
            </Field>
            <Field label={t("pages.bills_body.frequency_l")}>
              <select className="field-input" value={form.frequency} onChange={e => setForm({...form, frequency: e.target.value})}>
                {["monthly","quarterly","yearly"].map(c => <option key={c}>{c}</option>)}
              </select>
            </Field>
          </div>
          <div className="flex gap-6 text-sm">
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_recurring} onChange={e => setForm({...form, is_recurring: e.target.checked})}/> {t("pages.bills_body.recurring_checkbox")}</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_paid} onChange={e => setForm({...form, is_paid: e.target.checked})}/> {t("pages.bills_body.already_paid")}</label>
          </div>
          <div className="flex justify-end gap-2">
            <button type="button" className="btn-ghost" onClick={() => setOpen(false)}>{t("common.cancel")}</button>
            <button type="submit" className="btn-primary" data-testid="submit-bill-btn">{edit ? t("common.save") : t("common.add")}</button>
          </div>
        </form>
      </Modal>

      <ReminderModal
        bill={reminderBill}
        onClose={() => setReminderBill(null)}
        onSaved={() => { setReminderBill(null); load(); }}
      />
    </div>
  );
}

function ReminderModal({ bill, onClose, onSaved }) {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState(false);
  const [days, setDays] = useState(3);
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  // Sync local state whenever a new bill is opened
  useEffect(() => {
    if (bill) {
      setEnabled(!!bill.reminder_enabled);
      setDays(bill.reminder_days_ahead || 3);
    }
  }, [bill]);

  if (!bill) return null;

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/bills/${bill.id}/reminder-prefs`, {
        reminder_enabled: enabled,
        reminder_days_ahead: days,
      });
      toast.success(enabled ? `Reminder set · ${days} day${days === 1 ? "" : "s"} ahead` : "Reminder off");
      onSaved?.();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Could not save reminder";
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post(`/bills/${bill.id}/send-test-reminder`);
      if (data?.sent) {
        toast.success(t("toasts.test_email_sent"));
      } else {
        toast.error(data?.message || "Send failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setTesting(false); }
  };

  return (
    <Modal open={!!bill} onClose={onClose} title={`Reminders · ${bill.name}`}>
      <div className="grid gap-5" data-testid="reminder-modal">
        <label className="flex items-center justify-between gap-4 p-3 rounded-lg border border-[var(--border-default)]">
          <div>
            <div className="text-sm font-medium">Email me before this bill is due</div>
            <div className="text-xs text-[var(--text-secondary)] mt-0.5">
              A single reminder, sent to your account email. Nothing else.
            </div>
          </div>
          <input
            type="checkbox"
            checked={enabled}
            onChange={e => setEnabled(e.target.checked)}
            data-testid="reminder-enabled-checkbox"
            className="h-5 w-5 accent-[var(--accent-primary)]"
          />
        </label>

        <div>
          <div className="field-label mb-2">How many days ahead?</div>
          <div className="grid grid-cols-3 gap-2">
            {[1, 3, 7].map(n => (
              <button
                key={n}
                type="button"
                disabled={!enabled}
                onClick={() => setDays(n)}
                data-testid={`reminder-days-${n}`}
                className="p-3 rounded-lg border text-sm font-medium transition-all"
                style={{
                  borderColor: days === n && enabled ? "var(--accent-primary)" : "var(--border-default)",
                  background: days === n && enabled ? "var(--bg-accent)" : "var(--bg-card)",
                  color: enabled ? "var(--text-primary)" : "var(--text-muted)",
                  cursor: enabled ? "pointer" : "not-allowed",
                  opacity: enabled ? 1 : 0.55,
                }}
              >
                {n} day{n === 1 ? "" : "s"}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 flex-wrap">
          <button
            type="button"
            className="btn-ghost"
            onClick={sendTest}
            disabled={testing}
            data-testid="reminder-send-test-btn"
          >
            <Send size={14} /> {testing ? "Sending…" : "Send test email now"}
          </button>
          <div className="flex gap-2">
            <button type="button" className="btn-ghost" onClick={onClose}>Cancel</button>
            <button
              type="button"
              className="btn-primary"
              onClick={save}
              disabled={busy}
              data-testid="reminder-save-btn"
            >
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
}
